import {Injectable} from "@angular/core";
import {BehaviorSubject,Observable} from "rxjs";
import {Result} from "@domain/common/http/result";
import {environment} from "@environments/environment";
import {first,map} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {MontantDto} from "@domain/admin/entreprise/analytique/montantDto";
import {ExerciceDto} from "@domain/admin/entreprise/analytique/exerciceDto";
import {TypeBudget} from "@domain/budget/type-budget.enum";
import {SearchSpec} from "@domain/common/list-view/searchSpec";
import {FilterDTO} from "@domain/common/list-view/filterDTO";
import {Filter,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {RoleListe} from "@domain/workflow/roleListe";
import {Page} from "@domain/common/http/list-result";

/**
 * Service pour le détail des enveloppes
 *
 * @author Tom Jeammet
 * @date 01/07/2023
 */
@Injectable()
export class EnveloppeDetailService {

    /** Flag indiquant qu'une action est déjà en cours */
    private _isPending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    get isPending$(): Observable<boolean> { return this._isPending.asObservable(); }

    /**
     * Constructeur
     */
    constructor(
        private http: HttpClient,
    ) {
    }

    /**
     * Appel back pour récupérer la somme des imputations d'une enveloppe
     *
     * @param idEnveloppe l'ID de l'enveloppe
     * @param idMontant l'ID du montant utilisé
     * @param typeBudget le type de budget (réel=0 ou prévisionnel=1)
     */
    computeMontantTotalImpute(idEnveloppe: number,idMontant: number,typeBudget: TypeBudget): Observable<number> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/Budget/computeMontantTotalImpute?idEnveloppe=` + idEnveloppe + "&idMontant=" + idMontant + "&typeBudget=" + typeBudget,null).pipe(
            map(result => result?.data.montantTotalImpute)
        );
    }

    /**
     * Supprime l'analytique
     *
     * @param idAnalytique l'id de l'analytique
     */
    deleteAnalytique(idAnalytique: number): Observable<Result> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/Budget/deleteAnalytique?idAnalytique=${ idAnalytique }`, null);
    }

    /**
     * Sauvegarde un montant
     *
     * @param montant le montant
     */
    saveMontant(montant: MontantDto): Observable<Result> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/Budget/saveMontant`,montant);
    }

    /**
     * Supprime le montant
     */
    deleteMontant(idMontant: number): Observable<Result> {
        return this.http.post<Result>(`${environment.baseUrl}/controller/Budget/deleteMontant?idMontant=${idMontant}`,null);
    }

    /**
     * Récupère la liste des exercices
     */
    listeExercices(): Observable<ExerciceDto[]> {
        return this.http.get<Result>(`${environment.baseUrl}/controller/Budget/listeExercices`).pipe(
            map(result => result?.data.exercices)
        );
    }

    /**
     * Réaffecte le montant d'une enveloppe
     * @param idMontantSource id du montant initial
     * @param idMontantDestination id du montant cible
     * @param montant montant
     */
    reaffecter(idMontantSource: number, idMontantDestination: number, montant: number): Observable<Result> {
        return this.http.post<Result>(
            `${environment.baseUrl}/controller/Budget/doReaffectation?idMontantSource=${idMontantSource}&idMontantDestination=${idMontantDestination}&montant=${montant}`,null);
    }

    /**
     * Obtention de la liste des rôles responsables budgétaires
     *
     * @param idEnveloppe
     */
    listeRolesResponsablesBudgetaire(idEnveloppe: number): Observable<RoleListe[]> {
        //Création d'un filtre sur l'enveloppe concernée
        const filterEnveloppe: Filter = {
            clef: "listeLienRoleUser.listeEnveloppes.idEnveloppe",
            typeComparaison: TypeComparaison[TypeComparaison.LIKE],
            type: TypeFilter[TypeFilter.STRING],
            valeur: idEnveloppe.toString()
        };

        //Création d'un filtre sur la "spécificité" Responsable Budgétaire (36)
        const filterRoleOption: Filter = {
            clef: "listeRoleOption.typeRoleOption",
            typeComparaison: TypeComparaison[TypeComparaison.LIKE],
            type: TypeFilter[TypeFilter.STRING],
            valeur: "RESPONSABLE_BUDGET"
        };

        //Création d'un SearchSpec pour la requête
        const searchSpec: SearchSpec = {
            numPage: 0,
            nbObjetsParPage: 1000,
            listeFilter: [new FilterDTO(filterEnveloppe),new FilterDTO(filterRoleOption)],
            defaultOrder: "libelle"
        }

        //Exécution de l'appel
        return this.http.post<Page<RoleListe>>(`${environment.baseUrl}/controller/Role/listeAllRoles`,searchSpec).pipe(first(),map(page => page?.listeResultats as RoleListe[]));
    }

    /**
     * Une action est déjà en cours
     *
     * @param isPending true si une action est en cours
     */
    setPending(isPending: boolean): void {
        //Sauvegarde et émission de l'état
        this._isPending.next(isPending);
    }
}